import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

function Dashboard() {
  const navigate = useNavigate();
  const [orderdate, setOrderDate] = useState(moment().format("YYYY-MM-DD"));
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [stats, setStats] = useState({
    totalSales: 0,
    totalCredit: 0,
    msStock: 0,
    hsdStock: 0,
    totalLiters: 0,
    cashInHand: 0
  });

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user) setUserId(user.uid);
      else setUserId(null);
    });
    return () => unsubAuth();
  }, []);

  useEffect(() => {
    if (!userId) return;

    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // 1. Fetch Sales & Liters from daily_ledger
        const salesQ = query(
          collection(db, "daily_ledger"), 
          where("userId", "==", userId), 
          where("date", "==", orderdate)
        );
        const salesSnap = await getDocs(salesQ);
        let totalS = 0;
        let totalL = 0;
        salesSnap.forEach(doc => {
          totalS += Number(doc.data().totalCash || 0);
          totalL += Number(doc.data().qty || 0);
        });

        // 2. Fetch Credit (Udari) from customer_transactions
        const creditQ = query(
          collection(db, "customer_transactions"), 
          where("userId", "==", userId), 
          where("date", "==", orderdate),
          where("type", "==", "Credit")
        );
        const creditSnap = await getDocs(creditQ);
        let totalC = 0;
        creditSnap.forEach(doc => totalC += Number(doc.data().amount || 0));

        // 3. Fetch Stock Levels (MS vs HSD) from Tanks collection
        // This gets the most recent stock data available in the system
        const tankQ = query(collection(db, "tanks"), where("userId", "==", userId));
        const tankSnap = await getDocs(tankQ);
        let ms = 0;
        let hsd = 0;
        tankSnap.forEach(doc => {
          const data = doc.data();
          if (data.fuelType === "MS") ms += Number(data.currentStock || 0);
          if (data.fuelType === "HSD") hsd += Number(data.currentStock || 0);
        });

        setStats({
          totalSales: totalS,
          totalCredit: totalC,
          totalLiters: totalL,
          msStock: ms,
          hsdStock: hsd,
          cashInHand: totalS - totalC
        });
      } catch (err) {
        console.error("Dashboard Stats Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [userId, orderdate]);

  return (
    <div className="content-wrapper bg-light min-vh-100">
      <section className="content-header p-4">
        <div className="container-fluid d-flex justify-content-between align-items-center bg-white shadow-sm p-3 rounded">
          <div>
            <h3 className="font-weight-bold mb-0">Shrimati Padmavati Petroleum</h3>
            <span className="badge badge-info">Management Dashboard</span>
          </div>
          <div className="d-flex align-items-center">
            <label className="mr-2 mb-0 font-weight-bold">Select Date:</label>
            <input 
              type="date" 
              className="form-control" 
              style={{ width: '180px' }} 
              value={orderdate} 
              onChange={(e) => setOrderDate(e.target.value)} 
            />
          </div>
        </div>
      </section>

      <section className="content px-4">
        <div className="container-fluid">
          
          {/* REVENUE ROW */}
          <div className="row">
            <div className="col-md-3">
              <div className="card shadow border-0 bg-success text-white">
                <div className="card-body text-center">
                  <p className="text-uppercase small mb-1">Gross Sales</p>
                  <h2 className="font-weight-bold">₹ {stats.totalSales.toLocaleString()}</h2>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card shadow border-0 bg-warning text-dark">
                <div className="card-body text-center">
                  <p className="text-uppercase small mb-1">Total Udari (Credit)</p>
                  <h2 className="font-weight-bold">₹ {stats.totalCredit.toLocaleString()}</h2>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card shadow border-0 bg-danger text-white">
                <div className="card-body text-center">
                  <p className="text-uppercase small mb-1">Actual Cash In Hand</p>
                  <h2 className="font-weight-bold">₹ {stats.cashInHand.toLocaleString()}</h2>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card shadow border-0 bg-primary text-white">
                <div className="card-body text-center">
                  <p className="text-uppercase small mb-1">Volume Sold (Ltrs)</p>
                  <h2 className="font-weight-bold">{stats.totalLiters.toFixed(2)}</h2>
                </div>
              </div>
            </div>
          </div>

          {/* STOCK MONITORING */}
          <div className="row mt-4">
            <div className="col-md-6">
              <div className="card shadow border-0 p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="font-weight-bold mb-0 text-success">MS (PETROL) STOCK</h5>
                  <span className="badge badge-pill badge-success p-2">{stats.msStock.toLocaleString()} L</span>
                </div>
                <div className="progress rounded-pill" style={{ height: '15px' }}>
                  <div 
                    className={`progress-bar progress-bar-striped progress-bar-animated ${stats.msStock < 1000 ? 'bg-danger' : 'bg-success'}`} 
                    style={{ width: `${Math.min((stats.msStock / 20000) * 100, 100)}%` }}
                  ></div>
                </div>
                <small className="text-muted mt-1">Based on Underground Tank Configuration</small>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card shadow border-0 p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="font-weight-bold mb-0 text-primary">HSD (DIESEL) STOCK</h5>
                  <span className="badge badge-pill badge-primary p-2 text-white">{stats.hsdStock.toLocaleString()} L</span>
                </div>
                <div className="progress rounded-pill" style={{ height: '15px' }}>
                  <div 
                    className={`progress-bar progress-bar-striped progress-bar-animated ${stats.hsdStock < 1500 ? 'bg-danger' : 'bg-primary'}`} 
                    style={{ width: `${Math.min((stats.hsdStock / 25000) * 100, 100)}%` }}
                  ></div>
                </div>
                <small className="text-muted mt-1">Based on Underground Tank Configuration</small>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}

export default Dashboard;